import React, { useEffect, useState } from 'react'
import {
  createBurnOrder,
  createMintOrder,
  CreateOrderDTO,
  OrderModel,
  UpdateBurnDTO,
  updateBurnOrder
} from 'src/common/api'
import { ChainAsset, ChainName, FiatCurrency, RequestType, StagingChainAsset } from 'src/common/static'
import { alertNotification } from 'src/ui'
import { useAuth, useBlockchain } from 'src/common/context'
import { WalletModel } from 'src/common/api'
import { getBanksByIds } from 'src/common/api/banking/data'
import { getEnabledBanks } from 'src/common/api/organizations/data'
import { BankAccountModel } from 'src/common/api/banking/models'
import { logEvent } from '../../analytics/analytics'

interface Errors {
  inputError?: string
  walletError?: string
}

interface Order extends OrderModel {
  asset: ChainAsset
  chain: ChainName
}

const useRequest = (type: RequestType) => {
  const isMint = type === RequestType.MINT
  const isBurn = type === RequestType.BURN

  const { wallets } = useBlockchain()
  const { user } = useAuth()
  const [value, setValue] = useState('')
  const [asset, setAsset] = useState(ChainAsset.QCAD)
  const [selectedChain, setSelectedChain] = useState<ChainName>(ChainName.ETHEREUM)
  const [fiatCurrency, setFiatCurrency] = useState(FiatCurrency.CAD)

  const [selectedWalletId, setSelectedWalletId] = useState<string | null>(null)
  // Only show wallets flagged for the active operation: isMint for mint, isBurn for burn.
  const mintingWallets = wallets.filter((w) => {
    if (w.walletType !== 'MINTING' || w.chain !== selectedChain) return false
    if (isMint) return !!w.isMint
    if (isBurn) return !!w.isBurn
    return true
  })
  const selectedWallet =
    mintingWallets.find((w) => w.id === selectedWalletId) ??
    ({
      id: '',
      address: '',
      name: '',
      chain: selectedChain
    } as WalletModel)
  const selectedWalletBalance = selectedWallet?.balance
  const qcadWalletBalance = selectedWalletBalance?.qcad ?? selectedWalletBalance?.qcad_xlm ?? 0
  const isOptedIn = true // Blockchain details are always "opted in" (they exist)

  const [listedBanks, setListedBanks] = useState<BankAccountModel[]>([])
  const [selectedBankId, setSelectedBankId] = useState<string | null>(null)
  const selectedBank = listedBanks.find((b) => b.id === selectedBankId) ?? {
    id: '',
    name: '',
    externalId: '',
    externalName: '',
    label: '',
    bankDetails: null,
    hasAutomation: false,
    isClientDefault: false,
    isTrustAccount: false,
    accType: '',
    integrationType: '',
    createdAt: '',
    updatedAt: ''
  }

  const [modals, setModals] = useState({
    choosePayment: false,
    billpay: false,
    eft: false,
    interac: false,
    confirm: false,
    requestLoading: false,
    kycPortal: false,
    bankDetails: false
  })
  const [showPaymentInstructions, setShowPaymentInstructions] = useState(false)
  const [showBurnInstructions, setShowBurnInstructions] = useState(false)
  const [networkFee, setNetworkFee] = useState(0)
  const [errors, setErrors] = useState<Errors>({
    inputError: undefined,
    walletError: undefined
  })
  const [loading, setLoading] = useState(false)
  const [order, setOrder] = useState<Order | null>(null)
  const [createdBurnOrder, setCreatedBurnOrder] = useState<OrderModel | null>(null)

  const [anchorElAsset, setAnchorElAsset] = useState<null | HTMLElement>(null)
  const [anchorElChain, setAnchorElChain] = useState<null | HTMLElement>(null)
  const [anchorElFiatCurrency, setAnchorElFiatCurrency] = useState<null | HTMLElement>(null)

  const [anchorElWallet, setAnchorElWallet] = React.useState<null | HTMLElement>(null)
  const [anchorElBank, setAnchorElBank] = React.useState<null | HTMLElement>(null)

  useEffect(() => {
    const fetchBanks = async () => {
      try {
        const enabledResponse = await getEnabledBanks()
        const enabledBankIds = enabledResponse.enabledBankIds ?? []

        if (!enabledBankIds.length) {
          setListedBanks([])
          setSelectedBankId(null)
          return
        }

        const idsParam = enabledBankIds.join(',')
        const bankResponse = await getBanksByIds(idsParam)
        const banks = Array.isArray(bankResponse) ? bankResponse : []

        setListedBanks(banks)
        if (banks.length === 1) {
          setSelectedBankId(banks[0].id)
        }
      } catch {
        setListedBanks([])
        setSelectedBankId(null)
      }
    }

    fetchBanks()
  }, [])

  useEffect(() => {
    if (!mintingWallets.length) {
      setSelectedWalletId(null)
      return
    }

    // Auto-select if only one wallet exists
    if (mintingWallets.length === 1) {
      setSelectedWalletId(mintingWallets[0].id)
      return
    }

    // keep current selection only if still valid
    const stillValid = mintingWallets.some((w) => w.id === selectedWalletId)

    if (!stillValid) {
      setSelectedWalletId(null)
    }
  }, [selectedChain, wallets, selectedWalletId, mintingWallets])

  useEffect(() => {
    if (!listedBanks.length) {
      setSelectedBankId(null)
      return
    }

    // Auto-select if only one bank exists
    if (listedBanks.length === 1) {
      setSelectedBankId(listedBanks[0].id)
      return
    }

    // keep current selection only if still valid
    const stillValidBank = listedBanks.some((b) => b.id === selectedBankId)

    if (!stillValidBank) {
      setSelectedBankId(null)
    }
  }, [listedBanks, selectedBankId])

  const handleRequest = async () => {
    let inputError
    let walletError
    if (Number(value) === 0) {
      inputError = 'Must be greater than 0'
    }
    if (!selectedWallet.address) {
      walletError = 'Please choose a wallet first'
    }
    if (!selectedBank.id && isMint) {
      inputError = 'Please choose a bank'
    }
    if (!(inputError || walletError)) {
      setLoading(true)
      const start = performance.now()
      try {
        if (isMint) {
          // For mint, go directly to payment instructions
          setNetworkFee(0) // Currently network fee is 0
          setShowPaymentInstructions(true)
          logEvent('button_click', { buttonName: 'Mint Order Button' })
        } else if (isBurn) {
          // For burn, create the order first (to set up listener), then show instructions
          const order = await createOrder()
          // Store the created order but don't set it yet (to show instructions first)
          setCreatedBurnOrder(order)
          setShowBurnInstructions(true)
          logEvent('button_click', { buttonName: 'Burn Order Button' })
          const processingTime = (performance.now() - start) / 1000
          logEvent('order_processing_time', { orderId: order.id, processingTime_s: processingTime })
        }
        setLoading(false)
      } catch (err) {
        alertNotification("Something went wrong and your order couldn't be processed.")
        setLoading(false)
      }
      setErrors({ inputError, walletError })
    }
  }

  const createOrder = async (): Promise<OrderModel> => {
    try {
      if (!user?.organization_id) {
        throw new Error('Organization not found')
      }
      let asset: ChainAsset | StagingChainAsset
      if (
        process.env.REACT_APP_API_URL?.includes('staging') ||
        process.env.REACT_APP_API_URL?.includes('sandbox') ||
        process.env.REACT_APP_API_URL?.includes('localhost')
      ) {
        asset = StagingChainAsset.QCAD_TEST
      } else {
        asset = ChainAsset.QCAD
      }
      const orderRequestBody: CreateOrderDTO = {
        asset,
        fiatAmount: Number(value),
        fiatCurrency,
        cryptoAmount: Number(value),
        address: selectedWallet.address as string,
        chain: selectedChain,
        organizationId: user.organization_id
      }
      if (isMint && selectedBank.id) {
        orderRequestBody.selectedBankId = selectedBank.id
      }

      console.log(orderRequestBody)

      const order = isMint ? await createMintOrder(orderRequestBody) : await createBurnOrder(orderRequestBody)
      return order
    } catch (err) {
      throw new Error('Something went wrong. Please try again later.')
    }
  }

  const handlePaymentInstructionsProceed = async () => {
    const start = performance.now()
    setShowPaymentInstructions(false)
    handleOpen('requestLoading')
    logEvent('button_click', { buttonName: 'Proceed Button - Mint Payment Instructions' })
    try {
      const order = await createOrder()
      setOrder({ ...order, chain: selectedChain, asset })
      const processingTime = (performance.now() - start) / 1000
      logEvent('order_processing_time', { orderId: order.id, processingTime_s: processingTime })
    } catch (err) {
      alertNotification("Something went wrong and your order couldn't be processed.")
    }
    handleClose('requestLoading')
  }

  const handleBurnInstructionsProceed = async () => {
    setShowBurnInstructions(false)
    // Set the order that was created in handleRequest
    logEvent('button_click', { buttonName: 'Proceed Button - Burn Return Address Instructions' })
    if (createdBurnOrder) {
      setOrder({ ...createdBurnOrder, chain: selectedChain, asset })
      setCreatedBurnOrder(null)
    }
  }

  const handleConfirmRequest = async (stopLoading: () => void) => {
    if (isMint) {
      handleClose('confirm')
      handleOpen('requestLoading')
    }
    try {
      let order = await createOrder()
      if (isBurn) {
        handleClose('confirm')
        handleOpen('requestLoading')

        const orderRequestBody: UpdateBurnDTO = {
          orderId: order.id
        }
        order = await updateBurnOrder(orderRequestBody)
      }
      setOrder({ ...order, chain: selectedChain, asset })
    } catch (err) {
      alertNotification("Something went wrong and your order couldn't be processed.")
    }
    handleClose('requestLoading')
    stopLoading()
  }

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value
    if (Number(newValue) > 0 && errors.inputError) {
      setErrors((s) => ({ ...s, inputError: undefined }))
    }

    if (isBurn && asset === ChainAsset.QCAD) {
      if (
        process.env.REACT_APP_API_URL?.includes('staging') ||
        process.env.REACT_APP_API_URL?.includes('sandbox') ||
        process.env.REACT_APP_API_URL?.includes('localhost')
      ) {
        setErrors((s) => ({ ...s, inputError: undefined }))
      } else {
        if (Number(newValue) > qcadWalletBalance) {
          setErrors((s) => ({ ...s, inputError: 'Insufficient QCAD balance' }))
        }
      }
    }
    setValue(newValue)
  }

  const handleClose = (modal: string) => {
    setModals((s) => ({ ...s, [modal]: false }))
  }

  const handleOpen = (modal: string) => {
    setModals((s) => ({ ...s, [modal]: true }))
  }

  const handleOpenAssetMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElAsset(event.currentTarget)
  }

  const handleCloseAssetMenu = () => {
    setAnchorElAsset(null)
  }

  const chooseAsset = (asset: ChainAsset) => {
    setAsset(asset)
    setValue('')
    setErrors((s) => ({ ...s, inputError: undefined }))
    handleCloseAssetMenu()
  }

  const handleOpenFiatCurrencyMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElFiatCurrency(event.currentTarget)
  }

  const handleCloseFiatCurrencyMenu = () => {
    setAnchorElFiatCurrency(null)
  }

  const chooseFiatCurrency = (fiatCurrency: FiatCurrency) => {
    setFiatCurrency(fiatCurrency)
    setValue('')
    setErrors((s) => ({ ...s, inputError: undefined }))
    handleCloseFiatCurrencyMenu()
  }

  const handleOpenChainMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElChain(event.currentTarget)
  }

  const handleCloseChainMenu = () => {
    setAnchorElChain(null)
  }

  const chooseChain = (chain: ChainName) => {
    setSelectedChain(chain)
    setValue('')
    setErrors((s) => ({ ...s, inputError: undefined }))
    handleCloseChainMenu()
  }

  const handleOpenWalletMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElWallet(event.currentTarget)
  }

  const handleCloseWalletMenu = () => {
    setAnchorElWallet(null)
  }

  const handleOpenBankMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElBank(event.currentTarget)
  }

  const handleCloseBankMenu = () => {
    setAnchorElBank(null)
  }

  return {
    value,
    modals,
    asset,
    selectedChain,
    order,
    loading,
    errors,
    anchorElAsset,
    anchorElChain,
    fiatCurrency,
    anchorElFiatCurrency,
    networkFee,
    showPaymentInstructions,
    showBurnInstructions,
    handleRequest,
    handlePaymentInstructionsProceed,
    handleBurnInstructionsProceed,
    handleConfirmRequest,
    handleChange,
    handleClose,
    handleOpenAssetMenu,
    handleCloseAssetMenu,
    chooseAsset,
    handleOpenChainMenu,
    handleCloseChainMenu,
    chooseChain,
    handleOpenFiatCurrencyMenu,
    handleCloseFiatCurrencyMenu,
    chooseFiatCurrency,
    selectedWallet,
    selectedWalletBalance,
    isOptedIn,
    mintingWallets,
    setSelectedWalletId,
    selectedWalletId,
    handleOpenWalletMenu,
    handleCloseWalletMenu,
    anchorElWallet,
    handleOpenBankMenu,
    handleCloseBankMenu,
    anchorElBank,
    listedBanks,
    selectedBank,
    selectedBankId,
    setSelectedBankId
  }
}

export default useRequest
